import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getLogo } from '../services/api';
import { useAuth, getUserRole } from '../contexts/AuthContext';
import { useConferenceSettings } from '../contexts/ConferenceSettingsContext';
import { fetchSessions, fetchNews } from '../services/api';

const Navigation: React.FC = () => {
  const [isDark, setIsDark] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [sessions, setSessions] = useState<any[]>([]);
  const [hasNews, setHasNews] = useState(false);
  const { user, logout, isAuthenticated } = useAuth();
  const { settings } = useConferenceSettings();
  const navigate = useNavigate();

  useEffect(() => {
    const loadSessions = async () => {
      try {
        const sessionsData = await fetchSessions();
        setSessions(sessionsData.sessions || []);
      } catch (error) {
        console.error('Failed to load sessions:', error);
        setSessions([]);
      }
    };

    const checkForNews = async () => {
      try {
        // Check if we have a cached value and it's not expired
        const cachedNewsData = localStorage.getItem('newsCache');
        if (cachedNewsData) {
          const { hasNews: cachedHasNews, timestamp } = JSON.parse(cachedNewsData);
          const now = new Date().getTime();
          const cacheAge = now - timestamp;
          
          // Cache for 15 minutes (900000ms)
          if (cacheAge < 900000) {
            setHasNews(cachedHasNews);
            return;
          }
        }

        // If no cache or expired, fetch from API
        const response = await fetchNews();
        const today = new Date();
        // Check if there are any published news items with published dates in the past or today
        const hasPublishedNews = (response.news || []).some((item: any) => {
          return item.status === 'published' && new Date(item.published_date) <= today;
        });
        
        // Update state
        setHasNews(hasPublishedNews);
        
        // Store in cache with timestamp
        localStorage.setItem('newsCache', JSON.stringify({
          hasNews: hasPublishedNews,
          timestamp: new Date().getTime()
        }));
      } catch (error) {
        console.error('Failed to check for news:', error);
        // Try to get from cache if API fails
        try {
          const cachedNewsData = localStorage.getItem('newsCache');
          if (cachedNewsData) {
            const { hasNews: cachedHasNews } = JSON.parse(cachedNewsData);
            setHasNews(cachedHasNews);
          } else {
            setHasNews(false);
          }
        } catch (cacheError) {
          setHasNews(false);
        }
      }
    };

    loadSessions();
    checkForNews();
  }, []);

  // Basic navigation items always shown
  const baseNavigation = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Speakers', href: '/speakers' },
    { name: 'Presentations', href: '/presentations' },
    { name: 'Gallery', href: '/gallery' },
    ...(sessions.length > 0 ? [{ name: 'Schedule', href: '/schedule' }] : []),
    { name: 'Highlights', href: '/highlights' },
    { name: 'Awards', href: '/awards' },
    ...(hasNews ? [{ name: 'News', href: '/news' }] : []),
    { name: 'Location', href: '/location' },
  ];
  
  // Conditionally add CFP and Sponsorship based on settings
  const getNavigationItems = () => {
    const items = [...baseNavigation];
    
    if (settings?.accepting_cfp) {
      items.push({ name: 'Call for Speakers', href: '/cfp' });
    }
    
    if (settings?.accepting_sponsors) {
      items.push({ name: 'Sponsorship', href: '/sponsorship' });
    }
    
    return items;
  };

  const navigation = getNavigationItems();

  const toggleTheme = () => {
    setIsDark(!isDark);
    document.documentElement.classList.toggle('dark');
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const getDashboardLink = () => {
    if (!user) return '/dashboard';
    
    switch (getUserRole(user)) {
      case 'admin':
        return '/admin';
      case 'speaker':
        return '/speaker';
      default:
        return '/dashboard';
    }
  };

  return (
    <nav className="fixed top-0 w-full z-50 bg-white shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center">
            <picture>
              <source media="(min-width: 768px)" srcSet={getLogo()} />
              <img 
                src={getLogo(true)}
                alt="Language Intelligence Conference 2025" 
                className="h-10 md:h-12 lg:h-14"
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
            </picture>
          </Link>

          {/* Hamburger button */}
          <button
            onClick={toggleMenu}
            className="lg:hidden p-2 rounded-md hover:bg-gray-100 focus:outline-none"
            aria-label="Toggle menu"
          >
            <svg
              className="w-6 h-6 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {isMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>

          {/* Desktop menu */}
          <div className="hidden lg:flex items-center space-x-8">
            {navigation.map((item) => (
              <Link to={item.href} key={item.name} className="nav-link">{item.name}</Link>
            ))}
            
            {isAuthenticated ? (
              <>
                <Link to={getDashboardLink()} className="nav-link">
                  Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className="text-gray-700 hover:text-gray-900"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <div className="flex items-center space-x-4">
                <Link
                  to="/login"
                  className="text-gray-700 hover:text-gray-900"
                >
                  Sign In
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu */}
          <div
            className={`${
              isMenuOpen ? 'block' : 'hidden'
            } lg:hidden fixed top-16 left-0 right-0 bg-white shadow-lg z-50 w-full border-t border-gray-200`}
          >
            <div className="px-4 py-3 space-y-2 sm:px-6">
              {navigation.map((item) => (
                <Link 
                  to={item.href} 
                  key={item.name} 
                  className="block w-full px-3 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              
              {isAuthenticated ? (
                <>
                  <Link 
                  to={getDashboardLink()} 
                  className="block w-full px-3 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Dashboard
                </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                    className="w-full text-left px-3 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Sign In
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
